/* eslint-disable @typescript-eslint/camelcase */
import { ShipmentBookingMapper } from "@/data/persistences/mappers/ShipmentBookingMapper";
import { ShipmentBookingRepositoryInterface } from "@/data/persistences/repositories/contracts/ShipmentBookingRepositoryInterface";
import {
  CBPPickupListEntities,
  CBPPickupManifest,
  ShipmentBookingDetail,
  ShipmentBookingEntities,
  ShipmentPrefix,
  SttManualValidation,
  SttReferenceExtValidation,
  SttReferenceAdjustment,
  ValidateShipmentId
} from "@/domain/entities/ShipmentBooking";
import { Endpoints } from "../../misc/Endpoints";
import ApiService from "../../services/ApiService";
import { AddShipmentBookingRequestInterface } from "@/data/payload/contracts/ShipmentBookingRequest";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import {
  AddShipmentBooking,
  AddShipmentBookingV2,
  CreateShipmentInstantBookingDirectlyPayload,
  RequestValidateShipmentId,
  ShipmentInstantBooking
} from "@/data/payload/api/ShipmentBookingRequest";
import { AxiosResponse } from "axios";
import { RequestListOnProcessStt } from "@/data/payload/api/OnProcessApiRequest";
import {
  RequestListBooking,
  SttDetailRequest
} from "@/data/payload/api/BookingRequest";
import { isUrlUpdateVersion } from "../../misc/Utils";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";

export class ShipmentBookingApiRepository
  implements ShipmentBookingRepositoryInterface {
  private service: ApiService;
  private mapper: ShipmentBookingMapper;
  private endpoints: Endpoints;

  constructor(
    service: ApiService,
    mapper: ShipmentBookingMapper,
    endpoints: Endpoints
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }
  public async getManifestCBP(params: RequestListBooking): Promise<CBPPickupManifest> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getManifestCBP(params),
      {}
    );
    return this.mapper.convertDataCBPManifest(resp);
  }

  public async getBookingList(
    params: RequestListBooking
  ): Promise<ShipmentBookingEntities> {
    const resp = await this.service.invoke(
      "get",
      params.from === "payment" && isUrlUpdateVersion("paid-unpaid") === "v2"
        ? this.endpoints.getListSTTPaidUnpaidV2(params)
        : this.endpoints.getBookingList(params),
      {}
    );
    return this.mapper.convertShipmentBookingFromApi(resp);
  }

  public async getBookingListCBP(params: RequestListBooking): Promise<CBPPickupListEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getBookingListCBP(params),
      {}
    )
    return this.mapper.convertCBPPickupListEntities(resp);
  }

  public async getOnProcessSttList(
    params: RequestListOnProcessStt
  ): Promise<ShipmentBookingEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getOnProcessSttList(params),
      {}
    );
    return this.mapper.convertShipmentBookingFromApi(resp);
  }

  public async getBookingDetail(
    id: number,
    isWithEligibility: boolean,
    isAuth: boolean,
    pageType: string
  ): Promise<ShipmentBookingDetail> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getBookingDetail(id, isWithEligibility, isAuth, pageType),
      {}
    );
    const mapper =
      process.env.VUE_APP_MIGRATE_FROM_ELEXYS === "true"
        ? (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApiElexys(resp)
        : (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApi(resp);
    return mapper(resp);
  }

  public async getBookingDetailByStt(
    params: SttDetailRequest
  ): Promise<ShipmentBookingDetail> {
    const isNewEndpoint = FlagsPermissionBooking.config_is_new_endpont_detail_stt.isEnabled();

    const resp = await this.service.invoke(
      "get",
      isNewEndpoint
        ? this.endpoints.getBookingDetailByStt(params)
        : this.endpoints.getBookingDetail(
            params.id,
            params.isWithEligibility,
            params.isAuth,
            params.pageType,
            params.customStatus
          ),
      {}
    );
    const mapper =
      process.env.VUE_APP_MIGRATE_FROM_ELEXYS === "true"
        ? (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApiElexys(resp)
        : (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApi(resp);
    return mapper(resp);
  }

  public async getSttDetail(
    params: SttDetailRequest
  ): Promise<ShipmentBookingDetail> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getSttDetail(params),
      {}
    );
    const mapper =
      process.env.VUE_APP_MIGRATE_FROM_ELEXYS === "true"
        ? (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApiElexys(resp)
        : (resp: AxiosResponse) =>
            this.mapper.convertDetailShipmentBookingFromApi(resp);
    return mapper(resp);
  }

  async addBookingShipment(
    payload: AddShipmentBookingRequestInterface
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.addShipmentBooking(),
      undefined,
      payload as AddShipmentBooking
    );
    return this.mapper.convertPayloadData(resp);
  }

  async addBookingShipmentV2(
    payload: AddShipmentBookingRequestInterface
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.addShipmentBooking(),
      undefined,
      payload as AddShipmentBookingV2
    );
    return this.mapper.convertPayloadData(resp);
  }

  async addInstantBooking(
    payload: AddShipmentBookingRequestInterface
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.addShipmentBooking(2),
      undefined,
      payload as ShipmentInstantBooking
    );
    return this.mapper.convertPayloadData(resp);
  }

  async validateSttManual(
    clientId: number,
    sttManual: string,
    bookingType: string,
    productType: string
  ): Promise<SttManualValidation> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.validateSttManual(
        clientId,
        sttManual,
        bookingType,
        productType
      ),
      {}
    );
    return this.mapper.convertDataSttManualValidationFromApi(resp);
  }

  async validateSttReferenceExt(
    validateType: string,
    noRefExternal: string,
    shipmentId = "",
    clientCode = ""
  ): Promise<SttReferenceExtValidation> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.validateSttReferenceExt(
        validateType,
        noRefExternal,
        shipmentId,
        clientCode
      ),
      undefined,
      {
        toJSON: () => {
          // Dynamic Payload Body berdasarkan validateType
          if (validateType === "shipment_id") {
            return JSON.stringify({
              "validate_type": validateType,
              "shipment_id": shipmentId,
              "client_code": clientCode
            });
          }
          return JSON.stringify({
            "validate_type": validateType,
            "no_ref_external": noRefExternal
          });
        }
      }
    );
    return this.mapper.convertDataSttManualValidationFromApi(resp);
  }

  async getAdjustmentStt(
    sttNo: string
  ): Promise<SttReferenceAdjustment> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getAdjustmentStt(
        sttNo
      ),
      {}
    );
    return this.mapper.convertSttAdjustmentFromApi(resp);
  }

  async getListShipmentPrefix(): Promise<ShipmentPrefix[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListShipmentPrefix(),
      {}
    );
    return this.mapper.convertDataShipmentPrefixFromApi(resp);
  }

  async validateShipmentId(
    payload: RequestValidateShipmentId
  ): Promise<boolean> {
    await this.service.invoke(
      "post",
      this.endpoints.validateShipmentId(),
      undefined,
      payload
    );

    return true;
  }

  async validateShipmentIdWithResponse(
    payload: RequestValidateShipmentId
  ): Promise<ValidateShipmentId | null> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.validateShipmentId(),
      undefined,
      payload
    );
    return resp.data?.data
      ? this.mapper.convertValidateShipmentIdData(resp.data)
      : null;
  }

  
  async createShipmentInstantBookingDirectly(payload: any): Promise<any> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.createShipmentInstantBookingDirectly(),
      {},
      payload as CreateShipmentInstantBookingDirectlyPayload
    );
    
    return this.mapper.convertPayloadData(resp);
  }
}
